import React, { useRef, useEffect } from 'react';

/**
 * FFTScopeDisplay component
 * Displays a frequency spectrum analyzer visualization of FFT data
 */
const FFTScopeDisplay = ({ fftData, textColor, width = 300, height = 120 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Clear canvas completely
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (!fftData || fftData.length === 0) {
      // Draw flat line when no data
      ctx.strokeStyle = textColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, canvasHeight);
      ctx.lineTo(canvasWidth, canvasHeight);
      ctx.stroke();
      
      // Draw label
      ctx.fillStyle = textColor;
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No FFT data', canvasWidth / 2, canvasHeight / 2);
      return;
    }

    // Draw frequency spectrum bars
    const barWidth = canvasWidth / fftData.length;
    
    // Create gradient from green (low) to yellow (medium) to red (high)
    const gradient = ctx.createLinearGradient(0, canvasHeight, 0, 0);
    gradient.addColorStop(0, '#00ff00');    // Green at bottom
    gradient.addColorStop(0.6, '#ffff00');  // Yellow at 60%
    gradient.addColorStop(1, '#ff0000');    // Red at top

    for (let i = 0; i < fftData.length; i++) {
      const value = fftData[i];
      // Normalize value from 0-255 to 0-1
      const normalizedValue = value / 255;
      const barHeight = normalizedValue * canvasHeight;
      
      ctx.fillStyle = gradient;
      ctx.fillRect(i * barWidth, canvasHeight - barHeight, barWidth - 1, barHeight);
    }

    // Draw frequency grid lines
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.3;
    
    // Horizontal grid lines (magnitude)
    const numHorizontalLines = 5;
    for (let i = 0; i <= numHorizontalLines; i++) {
      const y = (i / numHorizontalLines) * canvasHeight;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
    }

    // Reset alpha
    ctx.globalAlpha = 1.0;
    
    // Draw labels
    ctx.fillStyle = textColor;
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Low Freq', 2, canvasHeight - 2);
    ctx.textAlign = 'right';
    ctx.fillText('High Freq', canvasWidth - 2, canvasHeight - 2);
    
  }, [fftData, textColor]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        border: `1px solid ${textColor}`,
        borderRadius: '4px',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      }}
    />
  );
};

export default FFTScopeDisplay;
